import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { TextInput } from '@core/components/Inputs';
import { SelectInput } from '@core/components/Select';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { Grid, Tooltip } from '@material-ui/core';
import { SelectBanco, SelectCategoria, SelectCliente } from 'app/components/Select';
import SelectConta from 'app/components/Select/SelectConta';
import SelectFornecedor from 'app/components/Select/SelectFornecedor';
import { useClienteInitialValue } from 'app/main/venda/components/modal/VendaInitialValues';
import { cancelarAlert } from 'app/services/alerts';
import { regraAutomaticaApi } from 'app/services/api/regraAutomaticaApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { ConciliacaoContext } from '../ConciliacaoContext';
import { ConciliacaoModal } from '../modal/ConciliacaoModal';

const schema = yup.object().shape({
  lancamento: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('o lançamento')),
  palavraChave: yup
    .string()
    .nullable()
    .required(mensagemCampoObrigatorio('a palavra-chave')),
  banco: yup
    .object()
    .nullable()
    .required(mensagemCampoObrigatorio('o banco')),

  categoria: yup
    .object()
    .nullable()
    .test('categoria', mensagemCampoObrigatorio('a categoria'), function(categoria) {
      if (this.parent.lancamento === 'MOVIMENTACAO_BANCARIA') {
        return !!categoria?.id;
      }
      return true;
    }),
  cliente: yup
    .object()
    .nullable()
    .test('cliente', mensagemCampoObrigatorio('o cliente'), function(cliente) {
      if (this.parent.lancamento === 'MOVIMENTACAO_BANCARIA' && this.parent.categoria?.natureza === 'RECEITA') {
        return !!cliente?.id;
      }
      return true;
    }),
  fornecedor: yup
    .object()
    .nullable()
    .test('fornecedor', mensagemCampoObrigatorio('o fornecedor'), function(fornecedor) {
      if (this.parent.lancamento === 'MOVIMENTACAO_BANCARIA' && this.parent.categoria?.natureza === 'DESPESA') {
        return !!fornecedor?.id;
      }
      return true;
    }),

  conta: yup
    .object()
    .nullable()
    .test('conta', mensagemCampoObrigatorio('o de-para'), function(conta) {
      if (this.parent.lancamento === 'TRANSFERENCIA') {
        return !!conta?.id;
      }
      return true;
    }),
});

const RegraAutomaticaCadastro = () => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useContext(ConciliacaoContext).modal;
  const [regraAutomatica, setRegrasAutomatica] = useContext(ConciliacaoContext).regraAutomatica;

  const CONCILIACAO = 'Conciliação';

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const title =
    'O sistema fará a busca das palavras-chaves cadastradas nas regras para conciliar seu extrato ainda mais rapidamente e de forma automática';

  useReduxLoading(loading);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  useEffect(() => {
    formik.setValues(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regraAutomatica]);

  const initialValues = {
    id: regraAutomatica.id || undefined,
    lancamento: regraAutomatica.lancamento || 'MOVIMENTACAO_BANCARIA',
    palavraChave: regraAutomatica.palavraChave || '',
    descricao: regraAutomatica.descricao || '',
    categoria: regraAutomatica.categoria || undefined,
    banco: regraAutomatica.banco || undefined,
    cliente: useClienteInitialValue(regraAutomatica.cliente) || undefined,
    fornecedor: regraAutomatica.fornecedor || undefined,
    conta: regraAutomatica.conta || undefined,
  };

  const makeParams = (regra) => {
    let entidade = {
      id: regra.id,
      lancamento: regra.lancamento,
      palavraChave: regra.palavraChave,
      bancoId: regra.banco.id,
      categoriaId: regra.categoria?.id,
      descricao: regra.descricao,
    };

    if (regra.conta) entidade.contaId = regra.conta.id;
    if (regra.fornecedor) entidade.fornecedorId = regra.fornecedor.id;
    if (regra.cliente) entidade.clienteId = regra.cliente.id;

    return entidade;
  };

  const salvar = async () => {
    if (!formik?.dirty) {
      close();
      return;
    }

    setLoading(true);

    let entidade = makeParams(formik.values);

    try {
      let response;

      if (entidade.id) {
        eventReactGA(CONCILIACAO, 'Atualizar regra automática');
        response = await regraAutomaticaApi().atualizar(entidade);
        if (response.data) {
          showSuccess('Regra automática editada com sucesso');
          close();
          setLoading(false);
        }
      } else {
        eventReactGA(CONCILIACAO, 'Adicionar regra automática');
        response = await regraAutomaticaApi().salvar(entidade);
        if (response.data) {
          showSuccess('Regra automática salva com sucesso');
          close();
          setLoading(false);
        }
      }
    } catch (err) {
      apiUtils.handleApiError(err);
      setLoading(false);
    }
  };

  const formik = useFormik({
    validationSchema: schema,
    initialValues: initialValues,
    onSubmit: salvar,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const _errors = {
    lancamento: (formik.touched?.lancamento || formik.submitCount > 0) && formik.errors?.lancamento,
    palavraChave: (formik.touched?.palavraChave || formik.submitCount > 0) && formik.errors?.palavraChave,
    descricao: (formik.touched?.descricao || formik.submitCount > 0) && formik.errors?.descricao,
    categoria: (formik.touched?.categoria || formik.submitCount > 0) && formik.errors?.categoria,
    banco: (formik.touched?.banco || formik.submitCount > 0) && formik.errors?.banco,
    cliente: (formik.touched?.cliente || formik.submitCount > 0) && formik.errors?.cliente,
    fornecedor: (formik.touched?.fornecedor || formik.submitCount > 0) && formik.errors?.fornecedor,
    conta: (formik.touched?.conta || formik.submitCount > 0) && formik.errors?.conta,
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const close = () => {
    setRegrasAutomatica({});
    formik.resetForm(initialValues);
    setModal(ConciliacaoModal.regrasAutomaticas);
  };

  const limpaCampo = (lancamento) => {
    let campos = {
      id: formik.values?.id,
      lancamento: lancamento,
      palavraChave: '',
      descricao: '',
      categoria: undefined,
      banco: undefined,
      cliente: undefined,
      fornecedor: undefined,
      conta: undefined,
    };
    formik.setValues(campos);
  };

  const Aviso = ({ children }) => {
    return (
      <Grid container style={{ padding: 16 }}>
        <FaInfoCircle size={18} style={{ color: '#f2994a', position: 'absolute' }} />
        <label style={{ paddingLeft: '25px' }}>{children}</label>
      </Grid>
    );
  };

  return (
    <>
      <SimpleDialog open={modal === ConciliacaoModal.cadastrarRegrasAutomaticas} onClose={handleClose} innerScroll>
        <FormikProvider value={formik}>
          <FormDialogContent
            onSubmit={(e) => {
              formik.handleSubmit();
            }}
            header={{
              title: 'Regras automáticas',
              toolbar: (
                <>
                  <CancelButton title="Cancelar" type="button" onClick={handleClose}>
                    Cancelar
                  </CancelButton>
                  <SaveButton color="buttonSave" type="submit" styles={undefined} variant={'contained'}>
                    Salvar
                  </SaveButton>
                </>
              ),
            }}
            content={
              <Grid container>
                <Aviso>Caso haja regras conflitantes, o sistema não irá realizar a conciliação automática.</Aviso>

                {formik.values?.lancamento === 'TRANSFERENCIA' && (
                  <Aviso>As transferências são realizadas conforme a natureza do lançamento no extrato bancário.</Aviso>
                )}

                <Grid container style={{ padding: 16 }} spacing={3}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <SelectInput
                      disableClearable={true}
                      id="lancamento"
                      label="Lançamento"
                      onChange={(e) => {
                        formik.handleChange(e);
                        limpaCampo(e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values?.lancamento}
                      options={[
                        { id: 'MOVIMENTACAO_BANCARIA', title: 'Movimentação bancária' },
                        { id: 'TRANSFERENCIA', title: 'Transferência' },
                      ]}
                      required
                      touched={!!_errors.lancamento}
                      error={_errors.lancamento}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <TextInput
                      id="descricao"
                      name="descricao"
                      label="Descrição"
                      onChange={formik.handleChange}
                      value={formik.values?.descricao}
                      fullWidth
                      variant="outlined"
                      maxLength={50}
                      touched={!!_errors.descricao}
                      error={_errors.descricao}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Tooltip title={title} placement="bottom">
                      <div>
                        <TextInput
                          id="palavraChave"
                          name="palavraChave"
                          label="Palavra-chave"
                          onChange={formik.handleChange}
                          value={formik.values?.palavraChave}
                          fullWidth
                          variant="outlined"
                          maxLength={50}
                          required
                          touched={!!_errors.palavraChave}
                          error={_errors.palavraChave}
                        />
                      </div>
                    </Tooltip>
                  </Grid>

                  {formik.values?.lancamento === 'MOVIMENTACAO_BANCARIA' ? (
                    <>
                      <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                        <SelectBanco
                          id="banco"
                          name="banco"
                          label="Banco"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values?.banco}
                          required
                          touched={!!_errors.banco}
                          error={_errors.banco}
                        />
                      </Grid>
                      <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                        <SelectCategoria
                          id="categoria"
                          label="Categoria"
                          value={formik.values?.categoria}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          maxLength={100}
                          required
                          touched={!!_errors.categoria}
                          error={_errors.categoria}
                        />
                      </Grid>
                      {formik.values?.categoria?.natureza === 'RECEITA' && (
                        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                          <SelectCliente
                            id="cliente"
                            label="Cliente*"
                            value={formik.values?.cliente}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            touched={!!_errors.cliente}
                            error={_errors.cliente}
                          />
                        </Grid>
                      )}
                      {formik.values?.categoria?.natureza === 'DESPESA' && (
                        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                          <SelectFornecedor
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            fornecedor={formik.values?.fornecedor}
                            error={_errors.fornecedor}
                            touched={!!_errors.fornecedor}
                            required
                          />
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                        <SelectConta
                          id="conta"
                          label="De-para"
                          value={formik.values?.conta}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          required
                          touched={!!_errors.conta}
                          error={_errors.conta}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <SelectBanco
                          id="banco"
                          name="banco"
                          label="Banco"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values?.banco}
                          required
                          touched={!!_errors.banco}
                          error={_errors.banco}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            }
          />
        </FormikProvider>
      </SimpleDialog>
    </>
  );
};

export default RegraAutomaticaCadastro;
